import type { SpaceWire } from 'types/space';

export interface WireOverlayLine {
  wireId: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  labelX: number;
  labelY: number;
  label: string;
}

/**
 * Pure: turns a document's declared `wires[]` into SVG line coordinates relative to a container,
 * given each wired component's measured `DOMRect` (from `[data-component-id]` in the canvas —
 * measurement itself is the caller's concern, this only does the geometry). Wires whose `from`/`to`
 * component has no measured rect (not currently rendered — e.g. inside a collapsed/unselected
 * branch) are skipped rather than drawn at the origin, which would read as a real connection.
 */
export function computeWireOverlayLines(
  wires: SpaceWire[] | undefined,
  rectsById: Map<string, DOMRect>,
  containerRect: DOMRect
): WireOverlayLine[] {
  if (!wires?.length) return [];

  return wires.reduce<WireOverlayLine[]>((lines, wire) => {
    const fromRect = rectsById.get(wire.from);
    const toRect = rectsById.get(wire.to);
    if (!fromRect || !toRect) return lines;

    const x1 = fromRect.left + fromRect.width / 2 - containerRect.left;
    const y1 = fromRect.top + fromRect.height / 2 - containerRect.top;
    const x2 = toRect.left + toRect.width / 2 - containerRect.left;
    const y2 = toRect.top + toRect.height / 2 - containerRect.top;

    lines.push({
      wireId: wire.id,
      x1,
      y1,
      x2,
      y2,
      labelX: (x1 + x2) / 2,
      labelY: (y1 + y2) / 2,
      label: `${wire.action} → ${wire.to}`,
    });
    return lines;
  }, []);
}
